import { FC, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import {
  BucketFileStatusEnum,
  IRenderingSignalR,
} from "Data/interfaces/ListagemArquivos/IListFilesResponse";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { allOtherExtensions, COORDLY_3D_VIEWER_EXTENSION } from "Utils/ViewerUtils";
import Tooltip from "../Tooltip";
import styles from "./InfoRender.module.scss";

export interface IInfoRender {
  status?: BucketFileStatusEnum;
  signalRender?: IRenderingSignalR;
  extension?: string;
  /** Status de cada conversão do IFC; ausentes em backend anterior. */
  autodeskStatus?: BucketFileStatusEnum | null;
  nativeStatus?: BucketFileStatusEnum | null;
}

type Engine = 'native' | 'autodesk';

const TOTAL_STAGES = 4;

const InfoRender: FC<IInfoRender> = ({
  status,
  signalRender,
  extension,
  autodeskStatus,
  nativeStatus,
}) => {
  const { t } = useTranslation(['viewer', 'panels']);
  const tooltipRender = (data: IRenderingSignalR) => (
    <div className={styles['tooltipRender']}>
      {data.etapa === 0 ? (
        <>
          <div className={styles['title']}>{t('panels:loadingRender.wait')}</div>
          <div className={styles['value']}>{t('panels:loadingRender.stageDefault')}</div>
        </>
      ) : (
        <>
          <div className={styles['title']}>{t('panels:loadingRender.stagePrefix')} {data.etapa}:</div>
          <div className={styles['value']}>
            {data.etapa === 1
              ? t('panels:loadingRender.stage1')
              : data.etapa === 2
                ? t('panels:loadingRender.stage2')
                : data.etapa === 3
                  ? t('panels:loadingRender.stage3')
                  : t('panels:loadingRender.stageUnknown')}
          </div>
          <div className={styles['percent']}>
            <CircularProgressbar
              className={styles['loading']}
              strokeWidth={14}
              styles={buildStyles({ pathColor: '#3547C8', trailColor: '#EDEFF2' })}
              value={data.progress}
              maxValue={100}
            />
            {data.progress}%
          </div>
        </>
      )}
    </div>
  );

  const isConvertible = !extension || !allOtherExtensions.includes(extension.toLocaleLowerCase());

  const renderProcess = useMemo(() => {
    // Status só para o que passa por conversão; o resto abre direto e pode ter
    // um status antigo de uma tentativa de render (webp/gif).
    if (isConvertible) {
      if (status === 1 && (!signalRender || signalRender.etapa !== 2)) {
        return BucketFileStatusEnum.Rendered;
      }
      if (status === 2) {
        return BucketFileStatusEnum.Rendering;
      }
      if (status === 3) {
        return BucketFileStatusEnum.Error;
      }
    }
    return BucketFileStatusEnum.NotRendered;
  }, [isConvertible, status, signalRender]);

  // Com motor, o chip leva o nome dele em cinza e só o ícone carrega o status.
  const chip = (icon: ReactNode, label: string, variant: string, engine?: boolean) => (
    <span className={`${styles['chip']} ${styles[variant]} ${engine ? styles['engineChip'] : ''}`}>
      {icon}
      {label}
    </span>
  );

  const renderedChip = (engine?: Engine) => chip(
    <CheckCircleFilled className={styles['chipIcon']} />,
    engine ? t(`engineBadge.${engine}`) : t('rendered'),
    'rendered',
    !!engine,
  );

  const errorChip = (engine?: Engine) => chip(
    <CloseCircleFilled className={styles['chipIcon']} />,
    engine ? t(`engineBadge.${engine}`) : t('renderError'),
    'error',
    !!engine,
  );

  const notRenderedChip = () => chip(
    <span className={`${styles['chipIcon']} ${styles['dashedCircle']}`} />,
    t('notRendered'),
    'notRendered',
  );

  const renderingChip = (signal: IRenderingSignalR, engine?: Engine) => {
    if (signal.etapa === 5) return errorChip(engine);

    const stage = Math.min(Math.max(signal.etapa, 1), TOTAL_STAGES);
    return (
      <Tooltip overlay={tooltipRender(signal)}>
        <span className={`${styles['chip']} ${styles['rendering']} ${engine ? styles['engineChip'] : ''}`}>
          <CircularProgressbar
            className={styles['chipIcon']}
            strokeWidth={16}
            styles={buildStyles({ pathColor: '#3547C8', trailColor: '#E3E6F8' })}
            value={stage}
            maxValue={TOTAL_STAGES}
          />
          {engine ? t(`engineBadge.${engine}`) : t('rendering')}
          <strong className={styles['stage']}>{stage}/{TOTAL_STAGES}</strong>
        </span>
      </Tooltip>
    );
  };

  // IFC pode ter as duas conversões. Com mais de uma em jogo, um chip por
  // motor; com uma só, o chip genérico abaixo basta.
  if (extension?.toLocaleLowerCase() === COORDLY_3D_VIEWER_EXTENSION && autodeskStatus !== undefined) {
    const lanes: [Engine, BucketFileStatusEnum | null][] = [
      ['native', nativeStatus ?? null],
      ['autodesk', autodeskStatus ?? BucketFileStatusEnum.NotRendered],
    ];
    const active = lanes.filter(([, laneStatus]) => (
      laneStatus === BucketFileStatusEnum.Rendered
      || laneStatus === BucketFileStatusEnum.Error
      || (laneStatus === BucketFileStatusEnum.Rendering && renderProcess === BucketFileStatusEnum.Rendering && signalRender)
    ));
    if (active.length > 1) {
      return (
        <div className={styles['chips']}>
          {active.map(([engine, laneStatus]) => (
            <span key={engine}>
              {laneStatus === BucketFileStatusEnum.Rendered
                ? renderedChip(engine)
                : laneStatus === BucketFileStatusEnum.Error
                  ? errorChip(engine)
                  : renderingChip(signalRender!, engine)}
            </span>
          ))}
        </div>
      );
    }
    if (active.length === 1 && active[0][1] !== BucketFileStatusEnum.Rendering) {
      return active[0][1] === BucketFileStatusEnum.Rendered ? renderedChip() : errorChip();
    }
  }

  if (renderProcess === BucketFileStatusEnum.Rendered) {
    return renderedChip();
  }

  if (renderProcess === BucketFileStatusEnum.Rendering && signalRender) {
    return renderingChip(signalRender);
  }

  if (renderProcess === BucketFileStatusEnum.Error) {
    return errorChip();
  }

  if (extension && isConvertible) {
    return notRenderedChip();
  }

  return <> - </>
}

export default InfoRender;
